<template>
  <div class="onboarding-checklist">
    <div class="onboarding-checklist__header">
      <h2 class="onboarding-checklist__title">
        {{ $t('onboarding.checklist.greeting', { name: firstName }) }}
      </h2>
      <p class="onboarding-checklist__subtitle">{{ $t('onboarding.checklist.subtitle') }}</p>
      <p class="onboarding-checklist__progress-label">
        {{ $t('onboarding.checklist.progress', { done: completedCount, total: steps.length }) }}
      </p>
      <div class="onboarding-checklist__progress">
        <div
          class="onboarding-checklist__progress-fill"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>
    </div>

    <div class="onboarding-checklist__steps">
      <Card
        v-for="step in steps"
        :key="step.id"
        size="sm"
        class="onboarding-checklist__step"
        :class="{
          'onboarding-checklist__step--complete': step.complete,
          'onboarding-checklist__step--open': openStep === step.id,
        }"
      >
        <button
          type="button"
          class="onboarding-checklist__step-header"
          @click="toggleStep(step.id)"
        >
          <span
            class="onboarding-checklist__step-indicator"
            :class="{ 'onboarding-checklist__step-indicator--done': step.complete }"
          >
            <BaseIcon v-if="step.complete" name="check" size="12" />
          </span>
          <span class="onboarding-checklist__step-label">{{ step.label }}</span>
          <BaseIcon
            :name="openStep === step.id ? 'chevron-up' : 'chevron-down'"
            size="14"
          />
        </button>
        <div v-if="openStep === step.id" class="onboarding-checklist__step-body">
          <p>{{ step.description }}</p>
          <Btn
            v-if="!step.complete"
            variant="primary"
            size="sm"
            :text="step.cta"
            @click="goTo(step.route)"
          />
        </div>
      </Card>
    </div>

    <div class="onboarding-checklist__footer">
      <Btn
        variant="plain"
        :text="$t('onboarding.checklist.dismiss')"
        @click="handleDismiss"
      />
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import { useUserStore } from '@/store/userStore';
  import { useOnboarding } from '@/composables/useOnboarding';
  import Btn from '@/components/partials/Btn.vue';
  import Card from '@/components/partials/Card.vue';
  import BaseIcon from '@/components/partials/BaseIcon.vue';

  export default {
    name: 'OnboardingChecklist',

    components: {
      Btn,
      Card,
      BaseIcon,
    },

    props: {
      onClose: {
        type: Function,
        default: null,
      },
    },

    emits: ['close'],

    setup(props, { emit }) {
      const router = useRouter();
      const userStore = useUserStore();
      const { progress, fetchStatus, dismissChecklist } = useOnboarding();
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const openStep = ref(null);

      const firstName = computed(
        () => userStore.userData?.first_name || userStore.user?.user_metadata?.first_name || ''
      );

      const steps = computed(() => {
        const p = progress.value || {};
        return [
          {
            id: 'profile',
            label: $t('onboarding.checklist.steps.profile'),
            description: $t('onboarding.checklist.steps.profile_description'),
            complete: p.profile_complete,
            cta: $t('onboarding.checklist.steps.profile_cta'),
            route: '/settings/organization',
          },
          {
            id: 'team',
            label: $t('onboarding.checklist.steps.team'),
            description: $t('onboarding.checklist.steps.team_description'),
            complete: p.team_complete,
            cta: $t('onboarding.checklist.steps.team_cta'),
            route: '/settings/organization?tab=team',
          },
          {
            id: 'products',
            label: $t('onboarding.checklist.steps.products'),
            description: $t('onboarding.checklist.steps.products_description'),
            complete: p.products_complete,
            cta: $t('onboarding.checklist.steps.products_cta'),
            route: '/products',
          },
          {
            id: 'funnel',
            label: $t('onboarding.checklist.steps.funnel'),
            description: $t('onboarding.checklist.steps.funnel_description'),
            complete: p.form_published,
            cta: $t('onboarding.checklist.steps.funnel_cta'),
            route: '/all-flows',
          },
        ];
      });

      const completedCount = computed(() => steps.value.filter((step) => step.complete).length);

      const progressPercent = computed(() => {
        if (!steps.value.length) return 0;
        return Math.round((completedCount.value / steps.value.length) * 100);
      });

      const setFirstIncompleteStep = () => {
        const firstIncomplete = steps.value.find((step) => !step.complete);
        openStep.value = firstIncomplete?.id || steps.value[steps.value.length - 1]?.id || null;
      };

      onMounted(async () => {
        try {
          await fetchStatus();
        } catch {
          // Non-blocking
        }
        setFirstIncompleteStep();
      });

      const toggleStep = (id) => {
        openStep.value = openStep.value === id ? null : id;
      };

      const goTo = (route) => {
        if (props.onClose) props.onClose();
        emit('close');
        router.push(route);
      };

      const handleDismiss = async () => {
        try {
          await dismissChecklist();
        } catch {
          // Still close on error
        }
        if (props.onClose) props.onClose();
        emit('close');
      };

      return {
        firstName,
        steps,
        completedCount,
        progressPercent,
        openStep,
        toggleStep,
        goTo,
        handleDismiss,
      };
    },
  };
</script>

<style scoped lang="scss">
  .onboarding-checklist {
    padding: 0.5rem 0 0;

    &__header {
      margin-bottom: 1.25rem;
    }

    &__title {
      @include fontSize('md');
      font-weight: 700;
      margin-bottom: 0.25rem;
    }

    &__subtitle {
      color: var(--slate-500);
      @include fontSize('sm');
      margin-bottom: 0.75rem;
    }

    &__progress-label {
      color: var(--slate-600);
      @include fontSize('sm');
      margin-bottom: 0.35rem;
    }

    &__progress {
      height: 4px;
      background: var(--slate-100);
      border-radius: 999px;
      overflow: hidden;
    }

    &__progress-fill {
      height: 100%;
      background: var(--brand-500);
      border-radius: 999px;
      transition: width 0.2s ease;
    }

    &__steps {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__step {
      overflow: hidden;

      &--complete {
        border-color: var(--green-200);
      }

      :deep(.card__body) {
        padding: 0;
      }
    }

    &__step-header {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.875rem 1rem;
      background: none;
      border: none;
      cursor: pointer;
      text-align: left;
      @include fontSize('sm');
      font-weight: 500;
    }

    &__step-indicator {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 2px dashed var(--slate-300);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &--done {
        border: none;
        background: var(--green-500);
        color: #fff;
      }
    }

    &__step-label {
      flex: 1;
    }

    &__step-body {
      padding: 0 1rem 1rem 2.75rem;
      color: var(--slate-600);
      @include fontSize('sm');

      p {
        margin-bottom: 0.75rem;
      }
    }

    &__footer {
      margin-top: 1.5rem;
      display: flex;
      justify-content: flex-end;
    }
  }
</style>
